/**
 * App - Entry point for the Nuvemshop embedded admin app.
 *
 * Bootstrap order (Nexo spec):
 *   1. connect(nexo) - await admin handshake
 *   2. getStoreInfo(nexo) - fetch store language/id
 *   3. iAmReady(nexo) - signal the admin that the app is mounted
 *   4. Render the full app tree
 *
 * After iAmReady, the admin sends ACTION_NAVIGATE_SYNC with the initial
 * route; NexoSyncRoute handles that subscription.
 *
 * ErrorBoundary from @tiendanube/nexo dispatches ACTION_LOG_ERROR on
 * uncaught errors. Required for App Store homologation.
 */

import { Box, Spinner, Text, ToastProvider } from '@nimbus-ds/components';
import { AppShell } from '@nimbus-ds/patterns';
import type { StoreInfoResponse } from '@tiendanube/nexo';
import { connect, getStoreInfo, iAmReady } from '@tiendanube/nexo';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from '@/components/error-boundary';
import { NexoProvider } from '@/components/nexo-provider';
import { NexoSyncRoute } from '@/components/nexo-sync-route';
import { Router } from '@/components/router';
import { SideNav } from '@/components/side-nav';
import nexoClient from '@/lib/nexo-client';

export default function App() {
  const [connected, setConnected] = useState(false);
  const [storeInfo, setStoreInfo] = useState<StoreInfoResponse | null>(null);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await connect(nexoClient);

        // Signal ready as soon as the handshake completes. This must NOT be
        // gated on getStoreInfo: if store info fails, the admin would otherwise
        // keep showing its "Ocorreu um erro" overlay even though the app mounted
        // and works. connect + iAmReady are the ready pair; store info is extra.
        iAmReady(nexoClient);
        setConnected(true);

        // Store info (locale) is best-effort, so a failure never blocks the app.
        try {
          const info = await getStoreInfo(nexoClient);
          setStoreInfo(info);
        } catch {
          // Keep the default locale.
        }
      } catch {
        // connect() only rejects when the admin handshake times out. In the real
        // admin, keep the loading state (the admin shows its own error) rather
        // than rendering an app that would just spam failing session-token calls.
        // In local dev there is no admin parent, so render standalone to stay
        // workable. Matches the Nexo docs' skeleton-on-failure pattern in prod.
        setConnected(import.meta.env.DEV);
      }
    };

    bootstrap().catch(console.error);
  }, []);

  if (!connected) {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        gap="4"
        height="100vh"
        justifyContent="center"
      >
        <Spinner size="large" />
        <Text>
          {import.meta.env.DEV ? 'Conectando ao Nuvemshop admin...' : ''}
        </Text>
      </Box>
    );
  }

  return (
    <ErrorBoundary nexo={nexoClient}>
      <NexoProvider storeInfo={storeInfo}>
        <ToastProvider>
          <BrowserRouter>
            <NexoSyncRoute>
              <AppShell menu={<SideNav />} menuExpanded>
                <AppShell.Header
                  leftSlot={
                    <Box alignItems="center" display="flex" gap="2">
                      <Text fontWeight="bold">VestiAI</Text>
                    </Box>
                  }
                  rightSlot={null}
                />
                <Router />
              </AppShell>
            </NexoSyncRoute>
          </BrowserRouter>
        </ToastProvider>
      </NexoProvider>
    </ErrorBoundary>
  );
}
